import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import { createPortal } from 'react-dom';
import { Link } from 'react-router-dom';
import { LinkContainer } from 'react-router-bootstrap';
import Dropdown from 'react-bootstrap/lib/Dropdown';
import MenuItem from 'react-bootstrap/lib/MenuItem';
import Modal from 'components/Modal/Modal';
import { Mail, MailCheck } from 'lucide-react';
import browseUserCardStyles from 'components/Browse/UserCard/BrowseUserCard.module.scss';
import styles from './InviteProjectChooser.module.scss';

const INVITE_LABEL = 'Invite';
const INVITE_ROW_ICON_SIZE = 18;
const ALREADY_INVITED_NOTE = 'Already invited';

function InviteProjectChooser({
  dropdown,
  modal,
  collaborations,
  onSendInvite,
  onCloseDropdown,
  onCloseModal,
  isBriefInviteDisabled,
}) {
  useEffect(() => {
    if (!dropdown) {
      return undefined;
    }
    const close = () => onCloseDropdown();
    window.addEventListener('scroll', close, true);
    window.addEventListener('resize', close);
    return () => {
      window.removeEventListener('scroll', close, true);
      window.removeEventListener('resize', close);
    };
  }, [dropdown, onCloseDropdown]);

  const dropdownPortal =
    dropdown && typeof document !== 'undefined'
      ? createPortal(
          <div
            style={{
              position: 'fixed',
              top: dropdown.rect.top,
              left: dropdown.rect.left,
              width: dropdown.rect.width,
              zIndex: 2000,
            }}
          >
            <Dropdown
              id={`browse-invite-dd-${dropdown.username}`}
              pullRight
              open
              onToggle={(nextOpen) => {
                if (!nextOpen) {
                  onCloseDropdown();
                }
              }}
              style={{ display: 'grid', borderWidth: '2px', fontWeight: 700 }}
              onClick={(e) => e.stopPropagation()}
            >
              <Dropdown.Toggle
                bsStyle="primary"
                noCaret
                style={{
                  width: '100%',
                  height: dropdown.rect.height,
                  minHeight: 0,
                  opacity: 0,
                  padding: 0,
                  margin: 0,
                  border: 'none',
                  boxShadow: 'none',
                  background: 'transparent',
                }}
              >
                {INVITE_LABEL}
              </Dropdown.Toggle>
              <Dropdown.Menu>
                <LinkContainer
                  to={`/${dropdown.username}/hire`}
                  onClick={onCloseDropdown}
                >
                  <MenuItem eventKey="new">Contact for new project</MenuItem>
                </LinkContainer>
                <MenuItem divider />
                <MenuItem header eventKey="header">
                  Request application to:
                </MenuItem>
                {collaborations.map((brief, key) => {
                  const disabled = isBriefInviteDisabled(brief);
                  return (
                    <MenuItem
                      key={brief.id != null ? brief.id : key}
                      eventKey={String(brief.id != null ? brief.id : key)}
                      disabled={disabled}
                      className={
                        disabled
                          ? styles.inviteChooserMenuItemInvited
                          : undefined
                      }
                    >
                      <div
                        className={
                          disabled
                            ? `${browseUserCardStyles.collaborationDropdownItem} ${styles.inviteChooserAlreadyInvited}`
                            : browseUserCardStyles.collaborationDropdownItem
                        }
                        onClick={
                          disabled
                            ? undefined
                            : () => onSendInvite(brief, dropdown.userId)
                        }
                        role="presentation"
                      >
                        {disabled ? (
                          <span className={styles.inviteChooserInvitedBlock}>
                            <MailCheck
                              className={styles.inviteChooserIconSent}
                              size={INVITE_ROW_ICON_SIZE}
                              aria-hidden
                            />
                            <span className={styles.inviteChooserInvitedBody}>
                              <span className={styles.inviteChooserJobText}>
                                {brief.text}
                              </span>
                              <span
                                className={
                                  styles.inviteChooserAlreadyInvitedNote
                                }
                              >
                                {ALREADY_INVITED_NOTE}
                              </span>
                            </span>
                          </span>
                        ) : (
                          <span className={styles.inviteChooserJobRow}>
                            <Mail
                              className={styles.inviteChooserIconAvailable}
                              size={INVITE_ROW_ICON_SIZE}
                              aria-hidden
                            />
                            <span className={styles.inviteChooserJobText}>
                              {brief.text}
                            </span>
                          </span>
                        )}
                      </div>
                    </MenuItem>
                  );
                })}
              </Dropdown.Menu>
            </Dropdown>
          </div>,
          document.body
        )
      : null;

  return (
    <>
      {dropdownPortal}
      <Modal show={!!modal} onExit={onCloseModal} backgroundExit closeButton>
        {modal ? (
          <div className={styles.inviteChooserBody}>
            <h2 className={styles.inviteChooserTitle}>Invite expert</h2>
            <p className={styles.inviteChooserHint}>
              Choose a job or start a new project.
            </p>
            <div className={styles.inviteChooserActions}>
              <Link
                to={`/${modal.username}/hire`}
                className={styles.inviteChooserJobBtn}
                onClick={onCloseModal}
              >
                Contact for new project
              </Link>
              <hr className={styles.inviteChooserDivider} />
              <p className={styles.inviteChooserSectionLabel}>
                Request application to:
              </p>
              {collaborations.map((brief, key) => {
                const disabled = isBriefInviteDisabled(brief);
                return (
                  <button
                    key={brief.id != null ? brief.id : key}
                    type="button"
                    className={
                      disabled
                        ? `${styles.inviteChooserJobBtn} ${styles.inviteChooserJobBtnInvited}`
                        : styles.inviteChooserJobBtn
                    }
                    disabled={disabled}
                    onClick={() => {
                      if (!disabled) {
                        onSendInvite(brief, modal.userId);
                      }
                    }}
                  >
                    {disabled ? (
                      <span className={styles.inviteChooserInvitedBlock}>
                        <MailCheck
                          className={styles.inviteChooserIconSent}
                          size={INVITE_ROW_ICON_SIZE}
                          aria-hidden
                        />
                        <span className={styles.inviteChooserInvitedBody}>
                          <span className={styles.inviteChooserJobText}>
                            {brief.text}
                          </span>
                          <span
                            className={styles.inviteChooserAlreadyInvitedNote}
                          >
                            {ALREADY_INVITED_NOTE}
                          </span>
                        </span>
                      </span>
                    ) : (
                      <span className={styles.inviteChooserJobRow}>
                        <Mail
                          className={styles.inviteChooserIconAvailable}
                          size={INVITE_ROW_ICON_SIZE}
                          aria-hidden
                        />
                        <span className={styles.inviteChooserJobText}>
                          {brief.text}
                        </span>
                      </span>
                    )}
                  </button>
                );
              })}
            </div>
          </div>
        ) : null}
      </Modal>
    </>
  );
}

InviteProjectChooser.propTypes = {
  dropdown: PropTypes.shape({
    username: PropTypes.string.isRequired,
    userId: PropTypes.string.isRequired,
    rect: PropTypes.shape({
      top: PropTypes.number.isRequired,
      left: PropTypes.number.isRequired,
      width: PropTypes.number.isRequired,
      height: PropTypes.number.isRequired,
    }).isRequired,
  }),
  modal: PropTypes.shape({
    username: PropTypes.string.isRequired,
    userId: PropTypes.string.isRequired,
  }),
  collaborations: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      text: PropTypes.node,
    })
  ),
  onSendInvite: PropTypes.func.isRequired,
  onCloseDropdown: PropTypes.func.isRequired,
  onCloseModal: PropTypes.func.isRequired,
  isBriefInviteDisabled: PropTypes.func,
};

InviteProjectChooser.defaultProps = {
  dropdown: null,
  modal: null,
  collaborations: [],
  isBriefInviteDisabled: () => false,
};

export default InviteProjectChooser;
